// src/services/pipelineService.js
// Enterprise Service module for Pipeline related API calls
//
// =============================================================================
// WHY DEPTH MATTERS HERE
// =============================================================================
// `pipelines` is the `internal_pipeline` source in
// spatialCoordinationService.detect4DConflicts. The scan's vertical filter is:
//
//     AND (p.depth_m IS NULL OR (p.depth_m <= ? AND p.depth_m >= ?))
//
// NULL depth is deliberately treated as in-scope — unknown is not safe. But
// that means an unsurveyed main raises a conflict at EVERY excavation depth,
// including a 0.4 m sidewalk cut over a 2.5 m trunk. If most of the register
// has no depth, every scan returns a wall of conflicts, operators learn to
// click through them, and the tool that was supposed to prevent strikes stops
// being read. Depth coverage is what keeps the clearance scan credible.
//
// getClearanceCoverage() below reports where you actually stand.
//
// ⚠️ BACKEND REQUIREMENT: adding depth_m to the form does nothing unless the
// pipelines INSERT/UPDATE in the backend model includes the column. If your
// pipelineModel builds an explicit column list, depth_m and status are silently
// dropped and every record still saves as NULL. Verify before trusting the UI.
// =============================================================================

import { authenticatedFetch } from './api';

const isDev = process.env.NODE_ENV === 'development';
const log = {
  info: (...a) => { if (isDev) console.log('[PipelineService]', ...a); },
  warn: (...a) => console.warn('[PipelineService]', ...a),
  error: (...a) => console.error('[PipelineService]', ...a),
};

// --- UPDATE: validation policy (shared with CreatePipelineForm) -------------
export const PIPELINE_VALIDATION = Object.freeze({
  // Matches SERVICE_AREA in spatialCoordinationService and the subsurface model.
  SERVICE_AREA: { minLat: 20.5, maxLat: 27.5, minLng: -80.5, maxLng: -72.0 },
  MIN_DEPTH_M: 0,
  MAX_DEPTH_M: 30,
  MAX_PATH_POINTS: 5000,
  MAX_DIAMETER_IN: 120,
});

export const PIPELINE_STATUSES = Object.freeze([
  { value: 'active',         label: 'Active',         scanned: true  },
  { value: 'abandoned',      label: 'Abandoned',      scanned: false },
  { value: 'decommissioned', label: 'Decommissioned', scanned: false },
  { value: 'removed',        label: 'Physically Removed', scanned: false },
]);

// Provenance for the depth figure. An as-built drawing and a field locate are
// not the same confidence, and a statutory register should say which it is.
export const DEPTH_SOURCES = Object.freeze([
  { value: 'field_survey', label: 'Field survey / locate' },
  { value: 'as_built',     label: 'As-built drawing' },
  { value: 'estimated',    label: 'Estimated from standard' },
]);

export class PipelineValidationError extends Error {
  constructor(message, details = []) {
    super(message);
    this.name = 'PipelineValidationError';
    this.code = 'VALIDATION_ERROR';
    this.details = details;
  }
}

const num = (v) => (typeof v === 'string' ? Number(v) : v);
const isNum = (v) => Number.isFinite(num(v));

// --- UPDATE: coordinate validation -----------------------------------------
/**
 * Validates a drawn or pasted pipeline path.
 *
 * The previous check was `typeof p.lat === 'number'`, which accepts 250 and
 * -900 as latitudes and cannot tell a transposed lat/lng pair from a real one.
 * A transposed path produces a perfectly valid record that renders in the wrong
 * hemisphere and never matches a clearance scan.
 *
 * @returns {{ errors: string[], warnings: string[], coordinates: Array|null }}
 */
export function validatePipelinePath(coords) {
  const errors = [];
  const warnings = [];

  if (!Array.isArray(coords) || coords.length < 2) {
    errors.push('A pipeline path needs at least 2 {lat, lng} points.');
    return { errors, warnings, coordinates: null };
  }
  if (coords.length > PIPELINE_VALIDATION.MAX_PATH_POINTS) {
    errors.push(`Path exceeds ${PIPELINE_VALIDATION.MAX_PATH_POINTS} points. Split into segments.`);
    return { errors, warnings, coordinates: null };
  }

  const clean = [];
  let outside = 0;

  coords.forEach((p, i) => {
    if (!p || typeof p !== 'object') { errors.push(`Point ${i} is not a {lat, lng} object.`); return; }

    const lat = num(p.lat);
    const lng = num(p.lng);

    // Number.isFinite, not truthiness — 0 is a legal coordinate.
    if (!isNum(p.lat) || lat < -90 || lat > 90) {
      errors.push(`Point ${i}: lat must be a number between -90 and 90 (got ${JSON.stringify(p.lat)}).`);
      return;
    }
    if (!isNum(p.lng) || lng < -180 || lng > 180) {
      errors.push(`Point ${i}: lng must be a number between -180 and 180 (got ${JSON.stringify(p.lng)}).`);
      return;
    }

    const sa = PIPELINE_VALIDATION.SERVICE_AREA;
    if (lat < sa.minLat || lat > sa.maxLat || lng < sa.minLng || lng > sa.maxLng) outside += 1;

    clean.push({ lat, lng });
  });

  if (errors.length > 0) return { errors, warnings, coordinates: null };

  if (new Set(clean.map(p => `${p.lat},${p.lng}`)).size < 2) {
    errors.push('All points are identical — the resulting line has zero length.');
    return { errors, warnings, coordinates: null };
  }

  if (outside > 0) {
    const sa = PIPELINE_VALIDATION.SERVICE_AREA;
    const swapFixes = clean.every(p =>
      p.lng >= sa.minLat && p.lng <= sa.maxLat && p.lat >= sa.minLng && p.lat <= sa.maxLng
    );
    warnings.push(
      `${outside} of ${clean.length} points fall outside the MOW-WSC service area.` +
      (swapFixes ? ' All points would fall inside it if lat and lng were swapped.' : '')
    );
  }

  return { errors, warnings, coordinates: clean };
}

// --- UPDATE: depth validation ----------------------------------------------
export function validatePipelineDepth(depth_m) {
  const errors = [];
  const warnings = [];

  if (depth_m === null || depth_m === undefined || depth_m === '') {
    warnings.push(
      'No cover depth recorded. The clearance scan treats unknown depth as in-scope, so this main will raise a conflict at every excavation depth.'
    );
    return { errors, warnings, value: null };
  }

  const d = num(depth_m);
  if (!Number.isFinite(d)) {
    errors.push('Cover depth must be a number.');
    return { errors, warnings, value: null };
  }
  if (d < PIPELINE_VALIDATION.MIN_DEPTH_M || d > PIPELINE_VALIDATION.MAX_DEPTH_M) {
    errors.push(`Cover depth must be between ${PIPELINE_VALIDATION.MIN_DEPTH_M} and ${PIPELINE_VALIDATION.MAX_DEPTH_M} metres.`);
    return { errors, warnings, value: null };
  }

  // Shallower than typical hand-tool depth — worth a second look, since it puts
  // the main inside the reach of almost any excavation.
  if (d > 0 && d < 0.3) {
    warnings.push(`${d} m is unusually shallow for a buried main. Confirm this is cover depth in metres, not invert elevation.`);
  }
  if (d > 6) {
    warnings.push(`${d} m is unusually deep. Confirm the unit is metres, not feet.`);
  }

  return { errors, warnings, value: d };
}

// --- UPDATE: payload normalisation -----------------------------------------
/**
 * Builds the API payload from raw form values, validating everything the
 * clearance scan depends on. Throws PipelineValidationError rather than letting
 * a malformed record reach the register.
 */
export function normalizePipelinePayload(formValues = {}) {
  const errors = [];
  const warnings = [];

  const name = String(formValues.name ?? '').trim();
  if (!name) errors.push('Pipeline designation is required.');

  const path = validatePipelinePath(formValues.path_coordinates);
  errors.push(...path.errors);
  warnings.push(...path.warnings);

  const depth = validatePipelineDepth(formValues.depth_m);
  errors.push(...depth.errors);
  warnings.push(...depth.warnings);

  let diameter = null;
  if (formValues.diameter_inches !== null && formValues.diameter_inches !== undefined && formValues.diameter_inches !== '') {
    const d = num(formValues.diameter_inches);
    if (!Number.isFinite(d) || d <= 0 || d > PIPELINE_VALIDATION.MAX_DIAMETER_IN) {
      errors.push(`Diameter must be between 0 and ${PIPELINE_VALIDATION.MAX_DIAMETER_IN} inches.`);
    } else diameter = d;
  }

  const status = formValues.status || 'active';
  if (!PIPELINE_STATUSES.some(s => s.value === status)) {
    errors.push(`Unknown status '${status}'.`);
  }

  // Depth provenance without a depth is meaningless; depth without provenance
  // is unverifiable. Flag the mismatch rather than silently accepting it.
  if (depth.value !== null && !formValues.depth_source) {
    warnings.push('Cover depth recorded without a source. Note whether it came from a field locate, an as-built, or an estimate.');
  }

  if (errors.length > 0) {
    throw new PipelineValidationError('Pipeline failed validation.', errors);
  }

  return {
    payload: {
      name,
      description: formValues.description || null,
      material: formValues.material || null,
      diameter_inches: diameter,
      install_date: formValues.install_date || null,
      path_coordinates: path.coordinates,
      area_code: formValues.area_code || null,
      // --- fields the clearance scan and cascade engine read ---
      depth_m: depth.value,
      depth_source: formValues.depth_source || null,
      status,
      pressure_zone: formValues.pressure_zone || null,
    },
    warnings,
  };
}

// --- Fetch ------------------------------------------------------------------
/**
 * Fetches pipeline data from the backend.
 *
 * --- UPDATE: reports integrity instead of failing silently.
 * The previous version logged a parse error to the console and set
 * path_coordinates to null. That pipeline then vanished from the map with no
 * user-visible signal at all. The counts are now attached to the returned array
 * as a non-enumerable `_integrity` property so existing callers that treat the
 * result as a plain array are unaffected.
 */
export const fetchPipelines = async (options = {}) => {
  const endpoint = options.endpoint || '/pipelines?all=true';
  log.info(`Fetching pipelines from: ${endpoint}`);

  try {
    const responseData = await authenticatedFetch(endpoint);
    const pipelines = Array.isArray(responseData) ? responseData : (responseData?.data || []);

    if (!Array.isArray(pipelines)) {
      log.error('fetchPipelines received non-array data:', pipelines);
      throw new Error('Received invalid data format for pipelines from server.');
    }

    const integrity = {
      total: pipelines.length,
      unparseableGeometry: 0,
      missingGeometry: 0,
      missingDepth: 0,
      outsideServiceArea: 0,
      notScanned: 0,
    };

    const processed = pipelines.map(pipeline => {
      let parsedCoords = null;

      if (typeof pipeline.path_coordinates === 'string') {
        try {
          const coords = JSON.parse(pipeline.path_coordinates);
          if (Array.isArray(coords)) parsedCoords = coords;
          else {
            integrity.unparseableGeometry += 1;
            log.warn(`Pipeline ${pipeline.id}: path_coordinates parsed to a non-array.`);
          }
        } catch (e) {
          integrity.unparseableGeometry += 1;
          log.error(`Pipeline ${pipeline.id}: path_coordinates is not valid JSON — it will not render on the map.`, e.message);
        }
      } else if (Array.isArray(pipeline.path_coordinates)) {
        parsedCoords = pipeline.path_coordinates;
      } else if (pipeline.path_coordinates != null) {
        integrity.unparseableGeometry += 1;
        log.warn(`Pipeline ${pipeline.id}: path_coordinates is neither string nor array.`);
      }

      if (!parsedCoords || parsedCoords.length < 2) integrity.missingGeometry += 1;

      const depth = pipeline.depth_m;
      if (depth === null || depth === undefined || depth === '') integrity.missingDepth += 1;

      const statusDef = PIPELINE_STATUSES.find(s => s.value === pipeline.status);
      if (statusDef && !statusDef.scanned) integrity.notScanned += 1;

      if (parsedCoords?.length) {
        const sa = PIPELINE_VALIDATION.SERVICE_AREA;
        const anyOutside = parsedCoords.some(p =>
          isNum(p?.lat) && isNum(p?.lng) &&
          (num(p.lat) < sa.minLat || num(p.lat) > sa.maxLat ||
           num(p.lng) < sa.minLng || num(p.lng) > sa.maxLng)
        );
        if (anyOutside) integrity.outsideServiceArea += 1;
      }

      return {
        ...pipeline,
        path_coordinates: parsedCoords,
        depth_m: depth === null || depth === undefined || depth === '' ? null : num(depth),
        // Convenience flag for the UI: does this main actually participate in
        // excavation clearance results?
        is_clearance_visible: !!(parsedCoords?.length >= 2) && (!statusDef || statusDef.scanned),
      };
    });

    Object.defineProperty(processed, '_integrity', { value: integrity, enumerable: false });

    if (integrity.unparseableGeometry > 0 || integrity.missingDepth > 0) {
      log.warn('Pipeline register integrity', integrity);
    }
    log.info(`Fetched and processed ${processed.length} pipelines.`, integrity);
    return processed;
  } catch (error) {
    log.error('Error fetching pipelines:', error);
    throw error;
  }
};

/**
 * --- UPDATE: register-wide clearance coverage.
 *
 * Mirrors the subsurface register's gap report. Run this on the pipelines
 * dashboard — a register at 100% row count and 30% depth coverage means most
 * clearance scans return conflicts that carry no information.
 */
export const getClearanceCoverage = async () => {
  const pipelines = await fetchPipelines();
  const integrity = pipelines._integrity || {};
  const total = pipelines.length || 0;

  const withDepth = total - (integrity.missingDepth || 0);
  const scanVisible = pipelines.filter(p => p.is_clearance_visible).length;

  return {
    total,
    scanVisible,
    scanVisiblePct: total ? Number(((100 * scanVisible) / total).toFixed(1)) : 100,
    withDepth,
    depthCoveragePct: total ? Number(((100 * withDepth) / total).toFixed(1)) : 0,
    integrity,
    // Ranked worklist: unsurveyed mains sorted by diameter, because a 24" trunk
    // with no depth is a far bigger clearance problem than a 2" service line.
    depthGaps: pipelines
      .filter(p => p.depth_m === null && p.is_clearance_visible)
      .sort((a, b) => (Number(b.diameter_inches) || 0) - (Number(a.diameter_inches) || 0))
      .slice(0, 200)
      .map(p => ({
        id: p.id,
        name: p.name,
        material: p.material,
        diameter_inches: p.diameter_inches,
        area_code: p.area_code,
      })),
  };
};

export const fetchPipelineById = async (id) => {
  if (!id) return null;
  const result = await authenticatedFetch(`/pipelines/${id}`);
  const p = result?.pipeline || result?.data || result;
  if (!p) return null;
  if (typeof p.path_coordinates === 'string') {
    try { p.path_coordinates = JSON.parse(p.path_coordinates); } catch { p.path_coordinates = null; }
  }
  return p;
};

/**
 * Creates a new pipeline.
 * --- UPDATE: validates before sending, and accepts depth/status/provenance.
 * @returns {Promise<object>} created pipeline, with `warnings` attached
 */
export const createPipeline = async (pipelineData) => {
  const { payload, warnings } = normalizePipelinePayload(pipelineData);
  log.info('Creating pipeline', { name: payload.name, depth_m: payload.depth_m, points: payload.path_coordinates.length });

  const result = await authenticatedFetch('/pipelines', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const created = result?.pipeline || result;

  // --- UPDATE: verify the backend actually persisted depth.
  // If the pipelines INSERT does not include depth_m, the API returns 201 and
  // the value is silently NULL. Surfacing that here is the difference between
  // "we recorded depth" and "we think we recorded depth."
  if (payload.depth_m !== null && created && created.depth_m == null) {
    warnings.push(
      'The server did not return a cover depth for this pipeline. depth_m may not be persisted by the backend — verify the pipelines INSERT includes the column.'
    );
    log.warn('depth_m was submitted but is absent from the created record', { id: created?.id });
  }

  return { ...created, warnings };
};

/**
 * Updates an existing pipeline.
 *
 * --- UPDATE: merges against the current record by default.
 * The endpoint is PUT, which conventionally means full replacement. Sending
 * only `{ name }` to a full-replace handler wipes depth_m, status, and
 * geometry — quietly removing a main from clearance scans as a side effect of
 * renaming it. Pass { merge: false } if your backend does partial updates and
 * you want to skip the extra read.
 */
export const updatePipeline = async (id, updateData, { merge = true } = {}) => {
  let body = updateData;

  if (merge) {
    const current = await fetchPipelineById(id);
    if (current) {
      body = {
        name: current.name,
        description: current.description,
        material: current.material,
        diameter_inches: current.diameter_inches,
        install_date: current.install_date,
        path_coordinates: current.path_coordinates,
        area_code: current.area_code,
        depth_m: current.depth_m,
        depth_source: current.depth_source,
        status: current.status,
        pressure_zone: current.pressure_zone,
        ...updateData,
      };
    }
  }

  // Re-validate whatever is being written, so an edit cannot introduce the
  // defects create() blocks.
  if (body.path_coordinates) {
    const path = validatePipelinePath(body.path_coordinates);
    if (path.errors.length) throw new PipelineValidationError('Pipeline update failed validation.', path.errors);
    body.path_coordinates = path.coordinates;
  }
  if (body.depth_m !== undefined) {
    const depth = validatePipelineDepth(body.depth_m);
    if (depth.errors.length) throw new PipelineValidationError('Pipeline update failed validation.', depth.errors);
    body.depth_m = depth.value;
  }

  log.info(`Updating pipeline ID: ${id}`, { merge, fields: Object.keys(updateData) });
  const result = await authenticatedFetch(`/pipelines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return result?.pipeline || result;
};

/**
 * --- UPDATE: depth-only helper.
 * The common back-office task is walking the depth-gap worklist and filling in
 * survey figures one main at a time.
 */
export const setPipelineDepth = async (id, depth_m, depth_source = null) => {
  const depth = validatePipelineDepth(depth_m);
  if (depth.errors.length) throw new PipelineValidationError('Invalid depth.', depth.errors);
  return updatePipeline(id, { depth_m: depth.value, depth_source }, { merge: true });
};

/**
 * --- UPDATE: status change helper.
 * Marking a main decommissioned removes it from clearance scans, so this is a
 * safety-relevant edit rather than a cosmetic one — hence its own function
 * with an explicit log line.
 */
export const setPipelineStatus = async (id, status) => {
  const def = PIPELINE_STATUSES.find(s => s.value === status);
  if (!def) throw new PipelineValidationError(`Unknown status '${status}'.`);
  if (!def.scanned) {
    log.warn(`Pipeline ${id} set to '${status}' — it will no longer appear in excavation clearance scans.`);
  }
  return updatePipeline(id, { status }, { merge: true });
};

/**
 * Deletes a pipeline and unlinks associated sensors.
 */
export const deletePipeline = async (id) => {
  log.info(`Deleting pipeline ID: ${id}`);
  // Deleting a main removes it from clearance scans permanently. Marking it
  // 'decommissioned' via setPipelineStatus keeps the record for investigations.
  return authenticatedFetch(`/pipelines/${id}`, { method: 'DELETE' });
};